import * as THREE from 'three';
import { animateTransform } from '../scene-transforms.js';

/**
 * Fixed, so materials never recompile as lights come and go: every lit material pays for at most this many point lights, in each pixel and
 * in the size of every lit shader the loading screen links.
 */
export const PROP_LIGHT_POOL = 4;

interface Source { light: THREE.PointLight; at: THREE.Vector3; distance: number }

/**
 * Fires, braziers, crystals and lit windows each build their own point light, but the whole map has far more of them than a
 * view can see, and every point light in the scene costs every lit pixel. This takes those lights out of the scene and keeps a
 * fixed pool of real ones near the camera instead: each frame the pool copies the nearest sources (their colour, reach and
 * whatever intensity their prop animated them to). A source keeps working as a plain object, so props need not know.
 */
export function poolPropLights(scene: THREE.Scene, roots: THREE.Object3D[]) {
  const sources: Source[] = [];
  for (const root of roots) {
    root.updateWorldMatrix(true, true);
    const found: THREE.PointLight[] = [];
    root.traverse(object => { if (object instanceof THREE.PointLight) found.push(object); });
    for (const light of found) {
      sources.push({ light, at: light.getWorldPosition(new THREE.Vector3()), distance: light.distance });
      light.removeFromParent();
    }
  }
  const pool = Array.from({ length: Math.min(PROP_LIGHT_POOL, sources.length) }, () => { const light = animateTransform(new THREE.PointLight(0xffffff, 0, 1, 2)); scene.add(light); return light; });
  const ranked: { source: Source; score: number }[] = sources.map(source => ({ source, score: 0 }));
  return {
    /** `focus` is the ground point at the centre of the view. */
    update(focus: { x: number; z: number }) {
      // Nearness counts from the edge of a light's reach, so a wide brazier ten metres off outranks a small lamp just behind.
      for (const entry of ranked) entry.score = Math.hypot(entry.source.at.x - focus.x, entry.source.at.z - focus.z) - entry.source.distance;
      ranked.sort((a, b) => a.score - b.score);
      pool.forEach((light, i) => {
        const source = ranked[i]?.source;
        if (!source) { light.intensity = 0; return; }
        light.position.copy(source.at); light.color.copy(source.light.color); light.distance = source.light.distance; light.decay = source.light.decay;
        light.intensity = source.light.intensity;
      });
    },
  };
}
